import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Box, Typography, Paper, CircularProgress, Alert,
    TextField, Select, MenuItem, FormControl, InputLabel, Button,
    List, ListItem, ListItemText, ListItemIcon, IconButton, Divider, Chip,
    Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Tooltip,
    Slide, LinearProgress, Collapse
} from '@mui/material';
import {
    Description, InsertDriveFile, PictureAsPdf, TableChart,
    ArrowUpward, ArrowDownward, Refresh,
    Visibility, Download, Edit, Delete,
    Close as CloseIcon, UploadFile,
    ExpandMore, ExpandLess
} from '@mui/icons-material';
import {
  collection, query, where, orderBy, onSnapshot, getDocs, doc, getDoc,
  updateDoc, serverTimestamp, writeBatch,
} from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase';
import { toast } from 'react-toastify';

import PublicQuestionnaire from './PublicQuestionnaire';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';

// Helper Functions (no changes here)
const formatDate = (ts) => {
    if (!ts) return '?';
    try {
        const date = ts.toDate ? ts.toDate() : new Date(ts);
        if (isNaN(date.getTime())) return 'Invalid Date';
        return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch (e) {
        console.error("FormatDate Error:", e);
        return '?';
    }
};

const formatFileSize = (b) => {
    if (b == null || isNaN(b)) return '';
    if (b === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    if (b < 0) return '?';
    const i = Math.max(0, Math.floor(Math.log(b) / Math.log(k)));
    return `${parseFloat((b / Math.pow(k, i)).toFixed(2))} ${sizes[i] || 'B'}`;
};

const USER_FACING_TYPES = {
    FINANCIAL: 'Financial',
    QUESTIONNAIRE: 'Questionnaire',
    OTHER: 'Other'
};

const getDocumentUserFacingType = (docType = '') => {
    const lowerDocType = docType.toLowerCase();
    if (lowerDocType.includes('financial')) return USER_FACING_TYPES.FINANCIAL;
    if (lowerDocType.includes('questionnaire')) return USER_FACING_TYPES.QUESTIONNAIRE;
    if (lowerDocType.includes('hr')) return USER_FACING_TYPES.OTHER;
    return USER_FACING_TYPES.OTHER;
};

const getProcessingStatusChip = (docItem) => {
    const status = docItem.documentStatus || 'unknown';
    
    switch (status) {
        case 'processing_failed':
            return (
                <Chip 
                    label="Processing Failed" 
                    size="small" 
                    color="error" 
                    variant="filled"
                    sx={{ cursor: 'pointer' }}
                    onClick={() => window.showProcessingError && window.showProcessingError(docItem)}
                />
            );
        case 'awaiting_sanitization':
        case 'processing':
            return (
                <Chip 
                    label="Processing..." 
                    size="small" 
                    color="info" 
                    variant="outlined"
                />
            );
        case 'sanitized':
        case 'processed':
        case 'completed':
            return (
                <Chip 
                    label="Ready" 
                    size="small" 
                    color="success" 
                    variant="outlined"
                />
            );
        default:
            return (
                <Chip 
                    label="Ready" 
                    size="small" 
                    color="default" 
                    variant="outlined"
                />
            );
    }
};

const getFileIcon = (docType = '', contentType = '') => {
    const userFacingType = getDocumentUserFacingType(docType);
    const lowerContentType = contentType.toLowerCase();

    if (userFacingType === USER_FACING_TYPES.FINANCIAL) {
        if (lowerContentType.includes('pdf')) return <PictureAsPdf sx={{ color: 'green' }} />;
        return <TableChart sx={{ color: 'green' }} />;
    }
    if (userFacingType === USER_FACING_TYPES.QUESTIONNAIRE) {
        if (docType === 'questionnaire_native') return <Description sx={{ color: 'purple' }} />;
        if (lowerContentType.includes('pdf')) return <PictureAsPdf sx={{ color: 'purple' }} />;
        return <Description sx={{ color: 'purple' }} />;
    }
    if (lowerContentType.includes('pdf')) return <PictureAsPdf color="error" />;
    if (lowerContentType.includes('excel') || lowerContentType.includes('spreadsheet') || lowerContentType.includes('csv')) {
        return <TableChart color="success" />;
    }
    if (lowerContentType.includes('document') || lowerContentType.includes('word')) {
        return <Description color="primary" />;
    }
    return <InsertDriveFile color="action" />;
};

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});


export default function UnifiedClientDocumentHistory({ clientId, user, defaultFilterType, clientIsActive = true, selectedClient }) {
    console.log('[DEBUG] UnifiedClientDocumentHistory received props:', { clientIsActive, clientId });
    const clientInSetup = selectedClient?.isSetupComplete === false;
    // State variables... (no changes here)
    const [allDocuments, setAllDocuments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [filterType, setFilterType] = useState('all');
    const [sortBy, setSortBy] = useState('updatedAt');
    const [sortOrder, setSortOrder] = useState('desc');
    const [docToDelete, setDocToDelete] = useState(null);
    const [viewingQuestionnaire, setViewingQuestionnaire] = useState(false);
    const [questionnaireViewData, setQuestionnaireViewData] = useState({
        responseId: null,
        templateId: null,
        initialData: null,
    });
    const [loadingQuestionnaireView, setLoadingQuestionnaireView] = useState(false);
    const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
    const [documentToUpdate, setDocumentToUpdate] = useState(null);
    const [updatedLabel, setUpdatedLabel] = useState('');
    const [isUpdating, setIsUpdating] = useState(false);
    const [newFile, setNewFile] = useState(null);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [expandedHistoryDocId, setExpandedHistoryDocId] = useState(null);
    const [loadingVersions, setLoadingVersions] = useState(false);
    const [isQuestionnaireHistoryExpanded, setIsQuestionnaireHistoryExpanded] = useState(false);
    const [versionsList, setVersionsList] = useState([]);
    const [errorModalOpen, setErrorModalOpen] = useState(false);
    const [errorDocument, setErrorDocument] = useState(null);
    // Data fetching and processing... (no changes in these functions)
    useEffect(() => {
  // Set the filter type based on the prop from the parent tab
  const validFilters = ['all', USER_FACING_TYPES.FINANCIAL, USER_FACING_TYPES.QUESTIONNAIRE, USER_FACING_TYPES.OTHER];
  setFilterType(validFilters.includes(defaultFilterType) ? defaultFilterType : 'all');
  
  if (!clientId) {
    setAllDocuments([]);
    setLoading(false);
    return;
  }

  setLoading(true);
  setError('');
  console.log(`[DEBUG] Setting up real-time document listener for client ID: ${clientId}`);

  const q = query(
    collection(db, 'documents'),
    where('clientId', '==', clientId),
    where('parentId', '==', null),
    orderBy('updatedAt', 'desc')  // KEEP THIS - Required for Firestore index
  );

  const unsubscribe = onSnapshot(
    q, 
    (querySnapshot) => {
      const docs = querySnapshot.docs.map(docData => ({
        id: docData.id,
        ...docData.data()
      }));
      console.log(`[DEBUG] Real-time update: Found ${docs.length} documents.`);
      setAllDocuments(docs);
      setLoading(false);
      setError('');  // Clear any previous errors on successful update
    }, 
    (err) => {
  // GRACEFUL HANDLING: If the error is 'permission-denied', it's likely because the
  // client was just deleted. We can silently clear the list and avoid showing an error.
  if (err.code === 'permission-denied') {
    console.log("[DEBUG] Listener permission denied, likely due to client deletion. Clearing document list.");
    setAllDocuments([]); // Clear the documents
    setError('');       // Clear any previous errors
  } else {
    // For all other legitimate errors, show the user a message.
    console.error("Error with real-time document listener: ", err);
    if (err.code === 'failed-precondition' && err.message.toLowerCase().includes('index')) {
      setError('Firestore index required. Check console for link.');
      toast.error('Database setup needed for sorting/filtering.');
    } else {
      setError("Failed to listen for document updates.");
      toast.error("Connection to document list failed.");
    }
    setAllDocuments([]);
  }
  setLoading(false);
}
  );

  // Cleanup function to detach the listener
  return () => {
    console.log("[DEBUG] Detaching real-time document listener.");
    unsubscribe();
  };
}, [clientId, defaultFilterType]); // Rerun when the client or default filter changes

    // Set up global error handler for processing status chips
    useEffect(() => {
        window.showProcessingError = (docItem) => {
            setErrorDocument(docItem);
            setErrorModalOpen(true);
        };
        return () => {
            delete window.showProcessingError;
        };
    }, []);

    const processedDocuments = useMemo(() => {
        let filteredDocs = [...allDocuments];
        if (searchTerm) {
            const s = searchTerm.toLowerCase();
            filteredDocs = filteredDocs.filter((docData) =>
                (docData.label || '').toLowerCase().includes(s) ||
                (docData.name || '').toLowerCase().includes(s) ||
                getDocumentUserFacingType(docData.type).toLowerCase().includes(s)
            );
        }
        if (filterType !== 'all') {
            filteredDocs = filteredDocs.filter(docData =>
                getDocumentUserFacingType(docData.type) === filterType
            );
        }
        
        const questionnaires = [];
        const otherDocuments = [];
        for (const doc of filteredDocs) {
            if (getDocumentUserFacingType(doc.type) === USER_FACING_TYPES.QUESTIONNAIRE) {
                questionnaires.push(doc);
            } else {
                otherDocuments.push({ ...doc, isGroup: false });
            }
        }

        let combinedList = [];
        if (questionnaires.length > 0) {
            const latestQuestionnaire = questionnaires[0];
            const history = questionnaires.slice(1);
            
            combinedList.push({
                id: 'questionnaire_group', 
                isGroup: true,
                latest: latestQuestionnaire,
                history: history,
            });
        }

        combinedList = [...combinedList, ...otherDocuments];

        return combinedList.sort((a, b) => {
            const aDate = a.isGroup ? a.latest.updatedAt : a.updatedAt;
            const bDate = b.isGroup ? b.latest.updatedAt : b.updatedAt;
            
            if (sortBy === 'updatedAt' || sortBy === 'createdAt') {
                const dateA = aDate?.toMillis() || 0;
                const dateB = bDate?.toMillis() || 0;
                return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
            }
            
            const valA = a.isGroup ? a.latest[sortBy] : a[sortBy];
            const valB = b.isGroup ? b.latest[sortBy] : b[sortBy];

            if (typeof valA === 'string' && typeof valB === 'string') {
                return sortOrder === 'desc' ? valB.localeCompare(valA) : valA.localeCompare(valB);
            }
            return 0;
        });

    }, [allDocuments, searchTerm, filterType, sortBy, sortOrder]);


    const handleDeleteConfirmOpen = (docItem) => setDocToDelete(docItem);
    const handleDeleteConfirmClose = () => setDocToDelete(null);

    const handleDeleteDocument = async () => {
  if (!docToDelete) return;

  const docLabel = docToDelete.label || docToDelete.name;
  const docId = docToDelete.id;
  setDocToDelete(null); // Close the confirmation dialog immediately

  const toastId = toast.loading(`Deleting "${docLabel}"...`);

  try {
    const deleteDocumentAndVersions = httpsCallable(functions, 'deleteDocumentAndVersions');
    const result = await deleteDocumentAndVersions({ documentId: docId });

    if (result.data.success) {
      toast.update(toastId, { 
        render: `Successfully deleted "${docLabel}"`, 
        type: "success", 
        isLoading: false, 
        autoClose: 5000 
      });
      // No need to manually refresh! The real-time listener will automatically remove it
    } else {
      throw new Error(result.data.message || 'The backend reported an error.');
    }
  } catch (err) {
    console.error("Error calling deleteDocumentAndVersions function:", err);
    toast.update(toastId, { 
      render: `Failed to delete document: ${err.message}`, 
      type: "error", 
      isLoading: false, 
      autoClose: 5000 
    });
  }
};

    // --- START OF FIXED FUNCTION ---
    const handleViewDocument = async (docItem) => {
    if (!clientIsActive) {
        toast.info("Actions are disabled for inactive clients.");
        return;
    }

    if (docItem.type === 'questionnaire_native') {
        // Check for the fields that actually exist (based on your Firestore document)
        if (!docItem.id || !docItem.questionnaireTemplateId || !docItem.nativeResponseId) {
            toast.error("Cannot view questionnaire. The document data is incomplete.");
            return;
        }

        console.log("Opening questionnaire viewer with document data:", docItem);
        
        setLoadingQuestionnaireView(true);
        setViewingQuestionnaire(true);
        
        try {
            // Fetch the actual answers from the subcollection using nativeResponseId
            const responseDoc = await getDoc(doc(db, 'clients', clientId, 'ceoQuestionnaireResponses', docItem.nativeResponseId));
            
            if (responseDoc.exists()) {
                const responseData = responseDoc.data();
                console.log("Fetched questionnaire response data:", responseData);
                
                // Convert the responses array to the expected object format
                const answersObj = {};
                if (Array.isArray(responseData.responses)) {
                    responseData.responses.forEach(resp => {
                        answersObj[resp.questionId] = resp.answer;
                    });
                }
                console.log("DEBUG: Converted answers object:", answersObj);
                // Fetch the template data
const templateDoc = await getDoc(doc(db, 'questionnaireTemplates', docItem.questionnaireTemplateId));
const templateData = templateDoc.exists() ? templateDoc.data() : null;

setQuestionnaireViewData({
    responseId: docItem.nativeResponseId,
    templateId: docItem.questionnaireTemplateId,
    templateData: templateData,
    initialData: answersObj
});
            } else {
                toast.error("Questionnaire response data not found.");
                setViewingQuestionnaire(false);
            }
        } catch (error) {
            console.error("Error fetching questionnaire data:", error);
            toast.error("Failed to load questionnaire data.");
            setViewingQuestionnaire(false);
        } finally {
            setLoadingQuestionnaireView(false);
        }
    } else if (docItem.url) {
        window.open(docItem.url, '_blank', 'noopener,noreferrer');
    } else {
        toast.info("No viewable content for this document type.");
    }
};

    // --- END OF FIXED FUNCTION ---
    
    const handleCloseQuestionnaireView = () => {
        setViewingQuestionnaire(false);
        setQuestionnaireViewData({ responseId: null, templateId: null, initialData: null });
    };

    // Other handlers (update, version history, etc.) remain unchanged...
    const handleOpenUpdateModal = (docItem) => {
        if (!clientIsActive) {
            toast.info("Actions are disabled for inactive clients.");
            return;
        }
        setDocumentToUpdate(docItem);
        setUpdatedLabel(docItem.label || docItem.name || '');
        setNewFile(null);
        setUploadProgress(0);
        setIsUpdateModalOpen(true);
    };

    const handleCloseUpdateModal = () => {
        setIsUpdateModalOpen(false);
        setDocumentToUpdate(null);
        setUpdatedLabel('');
        setNewFile(null);
        setUploadProgress(0);
        setIsUpdating(false);
    };

    const handleFileChange = (event) => {
        if (event.target.files && event.target.files[0]) {
            const file = event.target.files[0];
            setNewFile(file);
        } else {
            setNewFile(null);
        }
        if (event.target) {
            event.target.value = null;
        }
    };
    
    const shouldShowFileInput = (docType) => docType !== 'questionnaire_native';
    
    const handleUpdateDocument = async () => {
  if (!documentToUpdate) return;

  const finalLabel = updatedLabel.trim() || (newFile ? newFile.name : documentToUpdate.label);
  if (!finalLabel || !finalLabel.trim()) {
    toast.error("Please provide a label or select a new file version.");
    return;
  }
  
  // Prevent action if no actual changes are made
  if (!newFile && finalLabel.trim() === (documentToUpdate.label || documentToUpdate.name || '')) {
      toast.info("No changes were made.");
      handleCloseUpdateModal();
      return;
  }

  setIsUpdating(true);
  setUploadProgress(0);
  const toastId = toast.loading(`Updating "${finalLabel}"...`);

  try {
    const updateDocumentAndCreateVersion = httpsCallable(functions, 'updateDocumentAndCreateVersion');
    
    // Case 1: A new file version is being uploaded
    if (newFile) {
      const newStoragePath = `clients/${clientId}/documents/${documentToUpdate.id}/uploads/${new Date().getTime()}_${newFile.name}`;
      const storageFileRef = ref(storage, newStoragePath);
      const uploadTask = uploadBytesResumable(storageFileRef, newFile, { contentType: newFile.type });

      uploadTask.on('state_changed',
        (snapshot) => setUploadProgress((snapshot.bytesTransferred / snapshot.totalBytes) * 100),
        (error) => {
          console.error("Upload failed:", error);
          toast.update(toastId, { render: `File upload failed: ${error.message}`, type: 'error', isLoading: false, autoClose: 5000 });
          setIsUpdating(false);
        },
        async () => { // On upload success
          try {
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            const newFileData = {
              label: finalLabel,
              name: newFile.name,
              url: downloadURL,
              originalFilePathInStorage: newStoragePath,
              size: newFile.size,
              contentType: newFile.type,
            };
            
            const result = await updateDocumentAndCreateVersion({ documentId: documentToUpdate.id, newFileData });
            if (result.data.success) {
              toast.update(toastId, { render: `Successfully updated "${finalLabel}"`, type: 'success', isLoading: false, autoClose: 5000 });
              handleCloseUpdateModal();
            } else {
              throw new Error(result.data.message || 'Update failed');
            }
          } catch (error) {
            console.error("Error calling backend function:", error);
            toast.update(toastId, { render: `Failed to update: ${error.message}`, type: 'error', isLoading: false, autoClose: 5000 });
            setIsUpdating(false);
          }
        }
      );
    } else { // Case 2: Only the label is being updated
      const newFileData = { label: finalLabel };
      const result = await updateDocumentAndCreateVersion({ documentId: documentToUpdate.id, newFileData });
      if (result.data.success) {
        toast.update(toastId, { render: `Successfully updated "${finalLabel}"`, type: 'success', isLoading: false, autoClose: 5000 });
        handleCloseUpdateModal();
      } else {
        throw new Error(result.data.message || 'Update failed');
      }
    }
  } catch (err) {
    console.error("Error calling updateDocumentAndCreateVersion function:", err);
    toast.update(toastId, { render: `Failed to update document: ${err.message}`, type: 'error', isLoading: false, autoClose: 5000 });
    setIsUpdating(false);
  }
};
    
    const handleToggleHistory = async (docId) => {
        if (expandedHistoryDocId === docId) {
            setExpandedHistoryDocId(null);
            setVersionsList([]);
        } else {
            setExpandedHistoryDocId(docId);
            setVersionsList([]);
            setLoadingVersions(true);
            try {
                const versionsQuery = query(collection(db, 'documents', docId, 'versions'), orderBy('archivedAt', 'desc'));
                const querySnapshot = await getDocs(versionsQuery);
                const fetchedVersions = querySnapshot.docs.map(d => ({ id: d.id, ...d.data() }));
                setVersionsList(fetchedVersions);
            } catch (err) {
                console.error("Error fetching document versions:", err);
                toast.error("Failed to load version history.");
            } finally {
                setLoadingVersions(false);
            }
        }
    };
    
    const handleViewHistoricalVersion = (version) => {
        if (version.url) {
            window.open(version.url, '_blank', 'noopener,noreferrer');
        } else {
            toast.info("This historical version does not have a viewable file URL.");
        }
    };

    if (!clientId && !loading) {
        return null;
    }
    
    // JSX rendering code... (no changes here)
    const DocumentListItem = ({ docItem }) => {
    console.log('[DEBUG] Document item data:', { id: docItem.id, type: docItem.type, status: docItem.status, active: docItem.active, name: docItem.name });
        const userFriendlyType = getDocumentUserFacingType(docItem.type);
        const isActionable = clientIsActive || clientInSetup;
        const canView = isActionable && (docItem.url || docItem.type === 'questionnaire_native');
        const canDownload = isActionable && docItem.url && docItem.type !== 'questionnaire_native';
        const canEdit = isActionable;
        console.log('[DEBUG] Button states for', docItem.name, ':', { clientIsActive, canView, canDownload, canEdit, hasUrl: !!docItem.url });
        return (
            <ListItem
                secondaryAction={
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                        <Tooltip title={clientIsActive ? (docItem.type === 'questionnaire_native' ? "View Questionnaire" : "View File") : "Client Inactive"}>
                            <span>
                                <IconButton size="small" onClick={() => handleViewDocument(docItem)} disabled={!canView}>
                                    <Visibility fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                        {docItem.url && docItem.type !== 'questionnaire_native' && (
                            <Tooltip title={clientIsActive ? "Download File" : "Client Inactive"}>
                                <span>
                                    <IconButton size="small" href={canDownload ? docItem.url : undefined} target="_blank" download={docItem.name || 'download'} rel="noopener noreferrer" disabled={!canDownload} onClick={(e) => {if(!canDownload) e.preventDefault()}}>
                                        <Download fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        )}
                        <Tooltip title={clientIsActive ? "Update Document" : "Client Inactive"}>
                            <span>
                                <IconButton size="small" onClick={() => handleOpenUpdateModal(docItem)} disabled={!canEdit}>
                                    <Edit fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title={clientIsActive ? "Delete Document" : "Client Inactive"}>
                            <span>
                                <IconButton size="small" onClick={() => handleDeleteConfirmOpen(docItem)} disabled={!isActionable}>
                                    <Delete fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Box>
                }
                sx={{ pr: '180px' }}
            >
                <ListItemIcon sx={{minWidth: '40px'}}>
                    {getFileIcon(docItem.type, docItem.contentType)}
                </ListItemIcon>
                <ListItemText
                    primary={docItem.label || docItem.name || "Untitled Document"}
                    primaryTypographyProps={{ sx: { fontWeight: 'medium', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' } }}
                    secondary={
    <>
        <Typography component="span" variant="body2" color="text.primary">
            Type: <Chip label={userFriendlyType} size="small" variant="outlined" sx={{mr:1, cursor: 'default'}} />
            {docItem.type === 'questionnaire_native' && docItem.questionnaireTemplateVersion &&
                ` (Q.Ver: ${docItem.questionnaireTemplateVersion})`
            }
        </Typography>
        
        {/* NEW: Processing Status Display */}
        <Typography component="span" variant="body2" sx={{ display: 'block', mt: 0.5 }}>
            Status: {getProcessingStatusChip(docItem)}
        </Typography>
        
        <Typography component="span" variant="body2" color="text.secondary" sx={{ display: 'block' }}>
            Submitted: {formatDate(docItem.updatedAt || docItem.uploadedAt || docItem.createdAt)}
        </Typography>
        {docItem.name && (docItem.label !== docItem.name) &&
            <Typography variant="caption" color="text.disabled" display="block" sx={{whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>
                Filename: {docItem.name}
            </Typography>
        }
    </>
}
                />
            </ListItem>
        )
    }

    return (
        <Paper elevation={1} sx={{ p: {xs: 1, sm: 2}, mt: 2, position: 'relative' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="h6">Client Document History</Typography>
            </Box>

            <Paper variant="outlined" sx={{ p: 2, mb: 2, display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                <TextField label="Search Documents" variant="outlined" size="small" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} sx={{ flexGrow: 1, minWidth: '200px' }} />
                <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel>Filter by Type</InputLabel>
                    <Select value={filterType} label="Filter by Type" onChange={(e) => setFilterType(e.target.value)}>
                        <MenuItem value="all">All Types</MenuItem>
                        <MenuItem value={USER_FACING_TYPES.FINANCIAL}>{USER_FACING_TYPES.FINANCIAL}</MenuItem>
                        <MenuItem value={USER_FACING_TYPES.QUESTIONNAIRE}>{USER_FACING_TYPES.QUESTIONNAIRE}</MenuItem>
                        <MenuItem value={USER_FACING_TYPES.OTHER}>{USER_FACING_TYPES.OTHER}</MenuItem>
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel>Sort By</InputLabel>
                    <Select value={sortBy} label="Sort By" onChange={(e) => setSortBy(e.target.value)}>
                        <MenuItem value="updatedAt">Date Updated</MenuItem>
                        <MenuItem value="createdAt">Date Created</MenuItem>
                        <MenuItem value="label">Label</MenuItem>
                    </Select>
                </FormControl>
                <Button size="small" variant="outlined" onClick={() => setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')} startIcon={sortOrder === 'asc' ? <ArrowUpward /> : <ArrowDownward />}>
                    {sortOrder === 'asc' ? 'Asc' : 'Desc'}
                </Button>
            </Paper>

            {loading && <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}><CircularProgress /><Typography sx={{ml:1}}>Loading documents...</Typography></Box>}
            {error && !loading && <Alert severity="error" sx={{ my: 2 }}>{error}</Alert>}
            {!loading && !error && processedDocuments.length === 0 && (
                <Typography sx={{ py: 3, textAlign: 'center' }} color="text.secondary">
                    No documents match your current filters or no documents have been added for this client.
                </Typography>
            )}

            {!loading && !error && processedDocuments.length > 0 && (
                <List dense={false}>
                    {processedDocuments.map((item, index) => {
                        if (item.isGroup) {
                            const { latest, history } = item;
                            return (
                                <React.Fragment key={item.id}>
                                    <ListItem
                                        secondaryAction={
                                            <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                                                {history.length > 0 && (
                                                    <Tooltip title={isQuestionnaireHistoryExpanded ? "Hide History" : "Show History"}>
                                                        <Button
                                                            size="small"
                                                            onClick={() => setIsQuestionnaireHistoryExpanded(!isQuestionnaireHistoryExpanded)}
                                                            startIcon={isQuestionnaireHistoryExpanded ? <ExpandLess /> : <ExpandMore />}
                                                        >
                                                            History ({history.length})
                                                        </Button>
                                                    </Tooltip>
                                                )}
                                                <Tooltip title={(clientIsActive || clientInSetup) ? "View Questionnaire" : "Client Inactive"}><span><IconButton size="small" onClick={() => handleViewDocument(latest)} disabled={(!clientIsActive && !clientInSetup) || !latest}><Visibility fontSize="small" /></IconButton></span></Tooltip>
                                                <Tooltip title={(clientIsActive || clientInSetup) ? "Update Document" : "Client Inactive"}><span><IconButton size="small" onClick={() => handleOpenUpdateModal(latest)} disabled={(!clientIsActive && !clientInSetup) || !latest}><Edit fontSize="small" /></IconButton></span></Tooltip>
                                                <Tooltip title={(clientIsActive || clientInSetup) ? "Delete Document" : "Client Inactive"}><span><IconButton size="small" onClick={() => handleDeleteConfirmOpen(latest)} disabled={(!clientIsActive && !clientInSetup) || !latest}><Delete fontSize="small" /></IconButton></span></Tooltip>
                                            </Box>
                                        }
                                        sx={{pr: history.length > 0 ? '300px' : '180px'}}
                                    >
                                        <ListItemIcon sx={{minWidth: '40px'}}>{getFileIcon(latest.type, latest.contentType)}</ListItemIcon>
                                        <ListItemText
                                            primary={latest.label || latest.name || "Untitled Questionnaire"}
                                            primaryTypographyProps={{ sx: { fontWeight: 'bold' } }} 
                                            secondary={`Latest Submission: ${formatDate(latest.updatedAt || latest.createdAt)}`}
                                        />
                                    </ListItem>
                                    <Collapse in={isQuestionnaireHistoryExpanded} timeout="auto" unmountOnExit>
                                        <Paper variant="outlined" sx={{ my: 0.5, mx: 2, p: 1, bgcolor: 'grey.50' }}>
                                            <List dense disablePadding>
                                                {history.map(historyDoc => (
                                                    <DocumentListItem key={historyDoc.id} docItem={historyDoc} />
                                                ))}
                                            </List>
                                        </Paper>
                                    </Collapse>
                                </React.Fragment>
                            );
                        } else {
                            return (
                                <React.Fragment key={item.id}>
                                    <DocumentListItem docItem={item} />
                                    {index < processedDocuments.length - 1 && <Divider component="li" />}
                                </React.Fragment>
                            );
                        }
                    })}
                </List>
            )}

           {docToDelete && (
                <Dialog open={!!docToDelete} onClose={handleDeleteConfirmClose}>
                    <DialogTitle>Confirm Deletion</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            Are you sure you want to permanently delete the document "{docToDelete.label || docToDelete.name}"? This action may also delete related versions and cannot be undone.
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleDeleteConfirmClose}>Cancel</Button>
                        <Button onClick={handleDeleteDocument} color="error" autoFocus>Delete</Button>
                    </DialogActions>
                </Dialog>
            )}
            
            {viewingQuestionnaire && questionnaireViewData.initialData && (
                <Dialog fullWidth maxWidth="lg" open={viewingQuestionnaire} onClose={handleCloseQuestionnaireView} TransitionComponent={Transition}>
                    <DialogTitle sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                        {questionnaireViewData.initialData?.questionnaireTemplateName || 'Viewing Questionnaire'}
                        <IconButton edge="end" color="inherit" onClick={handleCloseQuestionnaireView} aria-label="close">
                            <CloseIcon />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent dividers>
                        {loadingQuestionnaireView ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                                <CircularProgress />
                                <Typography sx={{ ml: 2 }}>Loading Questionnaire Data...</Typography>
                            </Box>
                        ) : (
                            <PublicQuestionnaire
    template={questionnaireViewData.templateData}
    mode="view"
    initialResponses={questionnaireViewData.initialData}
    user={user}
    clientId={clientId}
    onSubmitSuccess={() => {}}
/>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseQuestionnaireView}>Close View</Button>
                    </DialogActions>
                </Dialog>
            )}
            
            {documentToUpdate && (
                <Dialog open={isUpdateModalOpen} onClose={handleCloseUpdateModal} fullWidth maxWidth="sm">
                    <DialogTitle>Update Document: {documentToUpdate.name || documentToUpdate.label}</DialogTitle>
                    <DialogContent>
                        <TextField 
                            autoFocus 
                            margin="dense" 
                            id="documentLabel" 
                            label="Document Label" 
                            type="text" 
                            fullWidth 
                            variant="outlined" 
                            value={updatedLabel} 
                            onChange={(e) => setUpdatedLabel(e.target.value)} 
                            helperText="This is how the document will be displayed in lists." 
                            sx={{mt:1, mb: 2}}
                        />
                        {shouldShowFileInput(documentToUpdate.type) && (
                            <Box sx={{mt: 2, mb:1, p:2, border: '1px dashed grey', borderRadius: 1}}>
                                <Typography variant="subtitle2" gutterBottom>Upload New Version (Optional)</Typography>
                                <Button variant="outlined" component="label" startIcon={<UploadFile />} fullWidth size="small">
                                    {newFile ? `Selected: ${newFile.name}` : "Choose File"}
                                    <input type="file" hidden onChange={handleFileChange} />
                                </Button>
                                {newFile && (
                                    <Box sx={{mt:1, display: 'flex', alignItems: 'center'}}>
                                        <Typography variant="caption" display="inline">
                                            {newFile.name} ({formatFileSize(newFile.size)})
                                        </Typography>
                                        <IconButton size="small" onClick={() => setNewFile(null)} sx={{ml:0.5}}>
                                            <CloseIcon fontSize="inherit"/>
                                        </IconButton>
                                    </Box>
                                )}
                                <Typography variant="caption" display="block" sx={{mt: 0.5}}>
                                    If you upload a new file, it will replace the current version.
                                </Typography>
                                {isUpdating && newFile && (
                                    <Box sx={{ width: '100%', mt: 2 }}>
                                        <LinearProgress variant="determinate" value={uploadProgress} />
                                        <Typography variant="caption" display="block" textAlign="center">
                                            {Math.round(uploadProgress)}%
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        )}
                    </DialogContent>
                    <DialogActions sx={{pb:2, pr:2}}>
                        <Button onClick={handleCloseUpdateModal} color="inherit" disabled={isUpdating}>Cancel</Button>
                        <Button 
                            onClick={handleUpdateDocument} 
                            variant="contained" 
                            color="primary" 
                            disabled={isUpdating || (!updatedLabel.trim() && !newFile) || (updatedLabel.trim() === (documentToUpdate.label || documentToUpdate.name || '') && !newFile)}
                        >
                            {isUpdating ? <CircularProgress size={24} color="inherit" /> : 'Save Changes'}
                        </Button>
                    </DialogActions>
                </Dialog>
            )}

            {/* Processing Error Modal */}
            {errorDocument && (
                <Dialog 
                    open={errorModalOpen} 
                    onClose={() => setErrorModalOpen(false)}
                    maxWidth="md"
                    fullWidth
                >
                    <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        Processing Error
                        <IconButton edge="end" color="inherit" onClick={() => setErrorModalOpen(false)}>
                            <CloseIcon />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent>
                        <Alert severity="error" sx={{ mb: 2 }}>
                            <Typography variant="h6" gutterBottom>
                                Document: {errorDocument.label || errorDocument.name || 'Unknown'}
                            </Typography>
                            <Typography variant="body1" component="div" sx={{ whiteSpace: 'pre-wrap' }}>
                                {errorDocument.processingError || 'An unknown processing error occurred.'}
                            </Typography>
                        </Alert>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setErrorModalOpen(false)} variant="contained">
                            Close
                        </Button>
                    </DialogActions>
                </Dialog>
            )}

        </Paper>
    );
}